import React, { useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, addDays, startOfWeek, endOfWeek, isSameDay, isToday } from 'date-fns';
import { eachDayOfInterval } from 'date-fns/eachDayOfInterval';
import { 
  LaunchTask, 
  TaskStatus, 
  TaskCategory, 
  TaskPriority, 
  LaunchCalendar,
  LaunchStage,
  getStageColor
} from '../types/launchpad';
import {
  getLaunchCalendar,
  createLaunchCalendar,
  updateTaskStatus,
  updateCalendarProgress,
  getAllLaunchTasks,
  getTasksDueToday,
  createCustomTask
} from '../services/supabase/launchpadService';
import PartnerRecommendations from '../components/launchpad/PartnerRecommendations';
import AddTaskModal from '../components/launchpad/AddTaskModal';
import LaunchDateSelector from '../components/launchpad/LaunchDateSelector';

const Launchpad: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  
  // Calendar and tasks state
  const [calendar, setCalendar] = useState<LaunchCalendar | null>(null);
  const [tasks, setTasks] = useState<LaunchTask[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [taskModalOpen, setTaskModalOpen] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<LaunchTask | null>(null);
  const [addTaskModalOpen, setAddTaskModalOpen] = useState<boolean>(false);
  const [launchDateModalOpen, setLaunchDateModalOpen] = useState<boolean>(false);
  const [userRegion, setUserRegion] = useState<string>('US');

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentDay, setCurrentDay] = useState<number>(1);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      toast.error('You must be logged in to access the Launchpad');
      navigate('/login');
    } else if (!loading && user) {
      loadCalendarData();
    }
  }, [isAuthenticated, loading, navigate, user]);

  // Load calendar data
  const loadCalendarData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Get calendar data
      const calendarData = await getLaunchCalendar(user.id);
      setCalendar(calendarData);
      
      if (calendarData) {
        setCurrentDay(calendarData.current_day);
        
        // Get tasks for the current week/month view
        await loadTasksForCurrentView();
        
        // Get tasks due today
        const todayTasksData = await getTasksDueToday(user.id);
        setTasks(todayTasksData);
      } else {
        // No calendar exists yet, show launch date selector
        setLaunchDateModalOpen(true);
      }
    } catch (error) {
      console.error('Error loading calendar data:', error);
      toast.error('Failed to load your launch calendar');
    } finally {
      setIsLoading(false);
    }
  };

  // Load tasks for the current calendar view
  const loadTasksForCurrentView = async () => {
    if (!user) return;
    
    try {
      let startDay, endDay;
      
      if (viewMode === 'week') {
        const start = startOfWeek(currentDate);
        const end = endOfWeek(currentDate);
        startDay = Math.max(1, calculateDayNumber(start));
        endDay = calculateDayNumber(end);
      } else {
        // Month view logic would go here
        startDay = Math.max(1, currentDay - 15);
        endDay = currentDay + 15;
      }
      
      const tasksData = await getAllLaunchTasks(user.id);
      setTasks(tasksData);
      
      // Update selected date tasks
      updateSelectedDateTasks(selectedDate, tasksData);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  // Calculate day number from date
  const calculateDayNumber = (date: Date): number => {
    if (!calendar) return 1;
    
    const startDate = new Date(calendar.start_date);
    const diffTime = Math.abs(date.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1; // Add 1 because day 1 is the start date
  };

  // Update tasks for selected date
  const updateSelectedDateTasks = (date: Date, allTasks: LaunchTask[]) => {
    const dayNumber = calculateDayNumber(date);
    const filteredTasks = allTasks.filter(task => task.scheduled_day === dayNumber);
    setTasks(filteredTasks);
  };

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    updateSelectedDateTasks(date, tasks);
  };

  // Handle task status update
  const handleTaskStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    try {
      const updatedTask = await updateTaskStatus(taskId, newStatus);
      if (updatedTask) {
        // Update tasks in state
        setTasks(prevTasks => 
          prevTasks.map(task => task.id === taskId ? { ...task, status: newStatus } : task)
        );
        
        // Update selected date tasks
        setTasks(prevTasks => 
          prevTasks.map(task => task.id === taskId ? { ...task, status: newStatus } : task)
        );
        
        toast.success('Task updated successfully');
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  // Handle adding a custom task
  const handleAddTask = async (taskData: {
    title: string;
    description: string;
    category: TaskCategory;
    priority: TaskPriority;
    scheduled_day: number;
    notes?: string;
    resources?: string[];
  }) => {
    if (!user || !calendar) return;
    
    try {
      setIsLoading(true);
      
      const newTask = {
        user_id: user.id,
        business_id: calendar.business_id,
        title: taskData.title,
        description: taskData.description,
        status: 'pending' as TaskStatus,
        priority: taskData.priority,
        category: taskData.category,
        due_date: new Date().toISOString(), // This will be calculated by the service
        scheduled_day: taskData.scheduled_day,
        notes: taskData.notes,
        resources: taskData.resources,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const createdTask = await createCustomTask(newTask);
      
      if (createdTask) {
        // Add the new task to the tasks list
        setTasks(prevTasks => [...prevTasks, createdTask]);
        
        // Show success message
        toast.success('Task added successfully!');
        
        // Update calendar progress
        await updateCalendarProgress(user.id, calendar.business_id);
        
        // Refresh calendar data
        await loadCalendarData();
      }
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new calendar with the selected launch date
  const handleLaunchDateSelect = async (launchDate: Date) => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setError(null); // Reset any previous errors

      // Check for existing calendar
      const existingCalendar = await getLaunchCalendar(user.id);
      
      if (existingCalendar) {
        // Update existing calendar with new launch date
        setCalendar(existingCalendar);
        setCurrentDay(existingCalendar.current_day);
        
        // Refresh calendar data
        await loadCalendarData();
        
        toast.success('Your business launch calendar has been updated!');
      } else {
        // If no calendar exists, create a new one
        const newCalendar = await createLaunchCalendar(user.id, undefined, launchDate.toISOString());
        
        if (newCalendar) {
          setCalendar(newCalendar);
          setCurrentDay(newCalendar.current_day);
          
          // Refresh calendar data
          await loadCalendarData();
          
          toast.success('Your business launch calendar has been created! Add tasks to start planning your launch.');
        } else {
          throw new Error('Failed to create launch calendar');
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      console.error('Error creating calendar:', error);
      setError(`Failed to create your launch calendar: ${errorMessage}. Please try again.`);
      toast.error(`Failed to set up launch calendar: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // No automatic task generation - tasks are created manually

  // Generate days for the calendar
  const generateCalendarDays = (): Date[] => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return eachDayOfInterval({ start, end });
    } else {
      // Month view logic would go here
      const startDate = addDays(currentDate, -15);
      const endDate = addDays(currentDate, 15);
      return eachDayOfInterval({ start: startDate, end: endDate });
    }
  };

  // Get tasks for a specific day
  const getTasksForDay = (day: Date): LaunchTask[] => {
    const dayNumber = calculateDayNumber(day);
    return tasks.filter(task => task.scheduled_day === dayNumber);
  };

  // Get stage for a specific day
  const getStageForDay = (day: Date): LaunchStage => {
    const dayNumber = calculateDayNumber(day);
    if (dayNumber <= 30) return LaunchStage.PLANNING;
    if (dayNumber <= 60) return LaunchStage.SETUP;
    if (dayNumber <= 120) return LaunchStage.DEVELOPMENT;
    if (dayNumber <= 150) return LaunchStage.PRE_LAUNCH;
    if (dayNumber <= 180) return LaunchStage.LAUNCH;
    if (dayNumber <= 270) return LaunchStage.POST_LAUNCH;
    return LaunchStage.GROWTH;
  };

  // Navigate to previous week/month
  const goToPrevious = () => {
    if (viewMode === 'week') {
      setCurrentDate(prev => addDays(prev, -7));
    } else {
      setCurrentDate(prev => addDays(prev, -30));
    }
  };

  // Navigate to next week/month
  const goToNext = () => {
    if (viewMode === 'week') {
      setCurrentDate(prev => addDays(prev, 7));
    } else {
      setCurrentDate(prev => addDays(prev, 30));
    }
  };

  // Effect to reload tasks when view changes
  useEffect(() => {
    if (calendar) {
      loadTasksForCurrentView();
    }
  }, [currentDate, viewMode, calendar]);

  // Open task details modal
  const openTaskModal = (task: LaunchTask) => {
    setSelectedTask(task);
    setTaskModalOpen(true);
  };

  // Render task priority indicator
  const renderPriorityIndicator = (priority: TaskPriority) => {
    const colors = {
      low: 'bg-gray-400',
      medium: 'bg-blue-400',
      high: 'bg-orange-400',
      critical: 'bg-red-500'
    };
    
    return (
      <span className={`inline-block w-3 h-3 rounded-full ${colors[priority]} mr-2`}></span>
    );
  };

  // Render task category badge
  const renderCategoryBadge = (category: TaskCategory) => {
    const colors: Record<TaskCategory, string> = {
      legal: 'purple',
      finance: 'green',
      marketing: 'blue',
      operations: 'indigo',
      product: 'yellow',
      sales: 'orange',
      hr: 'pink',
      admin: 'gray',
      research: 'teal',
      planning: 'cyan'
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${colors[category]}-100 text-${colors[category]}-800`}
      >
        {category.charAt(0).toUpperCase() + category.slice(1)}
      </span>
    );
  };

  // Render task status badge
  const renderStatusBadge = (status: TaskStatus) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      skipped: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      pending: 'Pending',
      in_progress: 'In Progress',
      completed: 'Completed',
      skipped: 'Skipped'
    };
    
    return (
      <span className={`inline-block px-2 py-1 text-xs rounded-full ${colors[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      
        {error && (
          <div className="bg-red-100/80 backdrop-blur-sm border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4 shadow-lg">
            <p>{error}</p>
          </div>
        )}
      
        {/* Section Title with 3D effect */}
        <div className="relative mb-10 text-center">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600 inline-block mb-2">
            Business Launchpad
          </h2>
          <div className="h-1 w-40 mx-auto rounded-full bg-gradient-to-r from-green-400 to-blue-500"></div>
        </div>
      
        <div className="flex justify-center mb-8">
          <div className="flex p-1 rounded-xl bg-gray-200/50 backdrop-blur-sm space-x-2">
            <button
              onClick={() => setLaunchDateModalOpen(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 font-medium"
            >
              {calendar ? 'Change Launch Date' : 'Set Launch Date'}
            </button>
            <button 
              onClick={() => setViewMode('week')} 
              className={`px-4 py-2 rounded-lg transition-all duration-300 font-medium ${viewMode === 'week' ? 'bg-white text-blue-600 shadow-md' : 'text-gray-600 hover:bg-white/50'}`}
            >
              Week
            </button>
            <button 
              onClick={() => setViewMode('month')} 
              className={`px-4 py-2 rounded-lg transition-all duration-300 font-medium ${viewMode === 'month' ? 'bg-white text-blue-600 shadow-md' : 'text-gray-600 hover:bg-white/50'}`}
            >
              Month
            </button>
          </div>
        </div>
      
      {calendar ? (
        <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 shadow-xl rounded-xl p-8 mb-10 overflow-hidden">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/40 to-teal-50/40 opacity-70"></div>
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-green-600">
                Day {currentDay}: {getStageForDay(currentDate)}
              </h2>
              <div className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm text-gray-600 font-medium">
                Started on {format(new Date(calendar.start_date), 'MMMM d, yyyy')}
              </div>
            </div>
          
          <div className="w-full bg-gray-200/50 backdrop-blur-sm rounded-full h-3 mb-6 shadow-inner">
            <div 
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full shadow-sm transition-all duration-700" 
              style={{ width: `${calendar.progress}%` }}
            ></div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-8">
            {Object.entries(calendar.category_progress).map(([category, progress]) => (
              <div key={category} className="bg-white/30 backdrop-blur-sm border border-white/40 p-4 rounded-xl shadow-md group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-800">{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                  <span className="text-sm px-2 py-0.5 bg-white/70 rounded-full font-medium text-gray-700">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200/50 backdrop-blur-sm rounded-full h-2 shadow-inner">
                  <div 
                    className={`bg-gradient-to-r from-${getCategoryColor(category as TaskCategory)}-400 to-${getCategoryColor(category as TaskCategory)}-600 h-2 rounded-full shadow-sm transition-all duration-500 group-hover:shadow`} 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Add Task Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setAddTaskModalOpen(true)}
              className="flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Custom Task
            </button>
          </div>
          </div>
        </div>
      ) : (
        <div className="relative backdrop-blur-sm bg-white/30 border border-white/40 shadow-xl rounded-xl p-10 mb-10 overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-blue-200/30 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-40 h-40 bg-indigo-200/30 rounded-full filter blur-3xl"></div>
          
          <div className="relative z-10 py-8 max-w-xl mx-auto">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center mx-auto mb-6 shadow-lg transform transition-transform hover:scale-105">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-3 text-center">Set Your Business Launch Date</h2>
            <p className="text-gray-700 mb-8 text-center text-lg">To get started with your business launch calendar, we need to know when you plan to officially launch your business.</p>
            <div className="text-center">
              <button
                onClick={() => setLaunchDateModalOpen(true)}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 font-medium inline-flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Set Launch Date
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <div className="relative backdrop-blur-sm bg-white/30 border border-white/40 shadow-xl rounded-xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/10 to-indigo-50/10 opacity-50"></div>
            <div className="relative z-10 p-4 bg-white/50 backdrop-blur-sm border-b border-white/40 flex justify-between items-center">
              <button 
                onClick={goToPrevious}
                className="p-2 rounded-full bg-white/60 hover:bg-white/80 shadow-sm hover:shadow transition-all duration-200 text-blue-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                {format(currentDate, 'MMMM yyyy')}
              </h2>
              
              <button 
                onClick={goToNext}
                className="p-2 rounded-full bg-white/60 hover:bg-white/80 shadow-sm hover:shadow transition-all duration-200 text-blue-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-7 gap-px bg-gray-200">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="bg-gray-50 p-2 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
              
              {generateCalendarDays().map((day: Date) => {
                const dayTasks = getTasksForDay(day);
                const stage = getStageForDay(day);
                const stageColor = getStageColor(stage);
                const isSelected = isSameDay(day, selectedDate);
                const isTodayDate = isToday(day);
                
                return (
                  <div 
                    key={day.toString()}
                    onClick={() => handleDateSelect(day)}
                    className={`bg-white p-2 min-h-[100px] border ${isSelected ? `border-${stageColor}-500` : 'border-transparent'} ${isTodayDate ? 'bg-blue-50' : ''} hover:bg-gray-50 cursor-pointer`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className={`text-sm font-medium ${isTodayDate ? 'text-blue-600' : 'text-gray-700'}`}>
                        {format(day, 'd')}
                      </span>
                      {dayTasks.length > 0 && (
                        <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full">
                          {dayTasks.length}
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1">
                      {dayTasks.slice(0, 2).map(task => (
                        <div 
                          key={task.id}
                          className={`text-xs truncate p-1 rounded ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-700'}`}
                        >
                          {renderPriorityIndicator(task.priority)}
                          {task.title}
                        </div>
                      ))}
                      {dayTasks.length > 2 && (
                        <div className="text-xs text-gray-500 p-1">
                          +{dayTasks.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Tasks for selected date */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow-md rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Tasks for {format(selectedDate, 'MMMM d, yyyy')}
            </h2>
            
            {tasks.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                No tasks scheduled for this day
                <div className="mt-4">
                  <button
                    onClick={() => setAddTaskModalOpen(true)}
                    className="text-blue-500 hover:text-blue-700 flex items-center mx-auto"
                  >
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add a task
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map(task => (
                  <div 
                    key={task.id} 
                    className="border rounded-md p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => openTaskModal(task)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className={`text-md font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                        {task.title}
                      </h3>
                      {renderStatusBadge(task.status)}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                    
                    <div className="flex justify-between items-center">
                      <div>
                        {renderCategoryBadge(task.category)}
                      </div>
                      
                      <div className="flex space-x-2">
                        {task.status !== 'completed' && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskStatusChange(task.id, 'completed');
                            }}
                            className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Complete
                          </button>
                        )}
                        {task.status === 'pending' && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskStatusChange(task.id, 'in_progress');
                            }}
                            className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          >
                            Start
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Today's tasks */}
          <div className="bg-white shadow-md rounded-lg p-4 mt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Today's Tasks
            </h2>
            
            {tasks.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                No tasks due today
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map(task => (
                  <div 
                    key={task.id} 
                    className="border rounded-md p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => openTaskModal(task)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-md font-medium text-gray-700">{task.title}</h3>
                      {renderPriorityIndicator(task.priority)}
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div>
                        {renderCategoryBadge(task.category)}
                      </div>
                      
                      <div className="flex space-x-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTaskStatusChange(task.id, 'completed');
                          }}
                          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Complete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Task Detail Modal */}
      {taskModalOpen && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">{selectedTask.title}</h2>
                <button 
                  onClick={() => setTaskModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <div className="flex space-x-2 mb-2">
                  {renderCategoryBadge(selectedTask.category)}
                  {renderStatusBadge(selectedTask.status)}
                </div>
                
                <div className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">Priority:</span> {selectedTask.priority.charAt(0).toUpperCase() + selectedTask.priority.slice(1)}
                </div>
                
                <div className="text-sm text-gray-600 mb-4">
                  <span className="font-medium">Day:</span> {selectedTask.scheduled_day}
                </div>
              </div>
              
              <div className="mb-4">
                <h3 className="text-md font-medium text-gray-700 mb-2">Description</h3>
                <p className="text-gray-600">{selectedTask.description}</p>
              </div>
              
              {/* Partner Recommendations */}
              {selectedTask.partner_recommendations && selectedTask.partner_recommendations.length > 0 && (
                <PartnerRecommendations 
                  partners={selectedTask.partner_recommendations} 
                  userRegion={userRegion}
                />
              )}
              
              {selectedTask.resources && selectedTask.resources.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-md font-medium text-gray-700 mb-2">Resources</h3>
                  <ul className="list-disc pl-5 text-gray-600">
                    {selectedTask.resources.map((resource, index) => (
                      <li key={index}>{resource}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedTask.notes && (
                <div className="mb-4">
                  <h3 className="text-md font-medium text-gray-700 mb-2">Notes</h3>
                  <p className="text-gray-600">{selectedTask.notes}</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end space-x-2">
              {selectedTask.status !== 'completed' && (
                <button 
                  onClick={() => {
                    handleTaskStatusChange(selectedTask.id, 'completed');
                    setTaskModalOpen(false);
                  }}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Mark as Completed
                </button>
              )}
              {selectedTask.status === 'completed' && (
                <button 
                  onClick={() => {
                    handleTaskStatusChange(selectedTask.id, 'in_progress');
                    setTaskModalOpen(false);
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Reopen Task
                </button>
              )}
              <button 
                onClick={() => setTaskModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Launch Date Selector Modal */}
      <LaunchDateSelector 
        isOpen={launchDateModalOpen}
        onClose={() => setLaunchDateModalOpen(false)}
        onDateSelect={handleLaunchDateSelect}
      />
      
      {/* Add Task Modal */}
      <AddTaskModal 
        isOpen={addTaskModalOpen}
        onClose={() => setAddTaskModalOpen(false)}
        onAddTask={handleAddTask}
        currentDay={currentDay}
      />
    </div>
  </section>
  );
};

// Helper function to get color for task category
const getCategoryColor = (category: TaskCategory): string => {
  switch (category) {
    case 'legal': return 'purple';
    case 'finance': return 'green';
    case 'marketing': return 'blue';
    case 'operations': return 'indigo';
    case 'product': return 'yellow';
    case 'sales': return 'orange';
    case 'hr': return 'pink';
    case 'research': return 'teal';
    case 'planning': return 'cyan';
    case 'admin':
    default: return 'gray';
  }
};

export default Launchpad;
